import React from 'react';

const sections = [
  {
    title: '1. Introduction',
    body: [
      'Welcome to Complin.AI (“the App”, “we”, “us”, “our”), operated by SNN Management Inc.',
      'These Terms and Conditions (“Terms”) govern your use of the Complin.AI mobile application, website, and related services (collectively, “Services”).',
      'By creating an account or using the App, you agree to these Terms. If you do not agree, please do not access or use Complin.AI.'
    ]
  },
  {
    title: '2. Purpose of the App',
    body: [
      'Complin.AI is a compliance management platform that helps users and organizations track compliance requirements and documentation, upload photos and evidence, review and manage vendor and regulatory obligations, and generate audit-ready records.',
      'The App is designed for professional and organizational use to simplify compliance tracking and documentation.'
    ]
  },
  {
    title: '3. User Accounts',
    body: [
      'To use certain features, you must create an account using email/password authentication or Google Single Sign-On (SSO).',
      'You agree to provide accurate and complete registration information, maintain the confidentiality of your credentials, and be responsible for all activities under your account.',
      'We reserve the right to suspend or terminate accounts that violate these Terms or misuse the App.'
    ]
  },
  {
    title: '4. Acceptable Use',
    body: [
      'You agree not to upload, share, or store unlawful, defamatory, or infringing materials; misuse AI or automated tools for unauthorized data processing; attempt to access restricted areas or disrupt system integrity; reverse-engineer, copy, or resell parts of the App or its code; or upload images or documents you do not have the right to share.',
      'We may suspend or disable accounts engaging in prohibited conduct.'
    ]
  },
  {
    title: '5. AI & Third-Party Services',
    body: [
      'Complin.AI uses Google Vision API and Firebase for document processing, image extraction, and data storage.',
      'While we take precautions to ensure data security, you acknowledge that third-party services may handle your uploaded content per their own policies.'
    ]
  },
  {
    title: '6. Data & Privacy',
    body: [
      'Your use of Complin.AI is also governed by our Privacy Policy, which explains how we collect, use, and protect your personal data.',
      'By using the App, you consent to our use of AI tools, Firebase storage, and third-party processing as described in the Privacy Policy.'
    ]
  },
  {
    title: '7. Subscription and Fees',
    body: [
      'Some features or storage tiers may require a paid subscription. Fees and billing cycles will be displayed before purchase. Subscriptions renew automatically unless canceled at least 24 hours before renewal, and payments are processed securely via Google Play, App Store, or authorized providers.',
      'We reserve the right to change pricing or add new tiers with advance notice.'
    ]
  },
  {
    title: '8. Intellectual Property',
    body: [
      'All materials in the App — including code, design, content, and trademarks — are owned or licensed by SNN Management Inc.',
      'You are granted a limited, non-exclusive, non-transferable license to use the App for internal business compliance management only. You may not copy, reproduce, redistribute, or use our name, logo, or trademarks without written consent.'
    ]
  },
  {
    title: '9. Termination',
    body: [
      'We may suspend or terminate your account if you violate these Terms, misuse data, attempt unauthorized access, engage in fraudulent activity, or fail to complete required payments.',
      'Upon termination, your access to stored data may be restricted or permanently deleted.'
    ]
  },
  {
    title: '10. Disclaimer of Warranties',
    body: [
      'Complin.AI is provided “as is” and “as available” without warranties of any kind. We do not guarantee the App will be error-free, uninterrupted, or that provided information will meet all regulatory requirements.',
      'AI analyses or extracted data may not always be accurate; you are responsible for verifying critical compliance information before relying on AI-generated insights.'
    ]
  },
  {
    title: '11. Limitation of Liability',
    body: [
      'To the fullest extent permitted by law, SNN Management Inc shall not be liable for indirect, incidental, or consequential damages, including loss of data, business interruption, or system errors.',
      'Our total liability for any claim shall not exceed the amount paid (if any) by you for the App within the last 12 months.'
    ]
  },
  {
    title: '12. Indemnification',
    body: [
      'You agree to indemnify and hold SNN Management Inc harmless from claims, damages, or liabilities arising from misuse of the App, violation of these Terms, or infringement of intellectual property or privacy rights.'
    ]
  },
  {
    title: '13. Modifications',
    body: [
      'We may update these Terms periodically. When changes occur, we will notify users via the App or email. Continued use after such changes constitutes acceptance of the revised Terms.'
    ]
  },
  {
    title: '14. Governing Law',
    body: [
      'These Terms are governed by the laws of the State of Utah, USA, without regard to conflict-of-law principles. Any disputes shall be resolved in courts located in Salt Lake County, Utah.'
    ]
  },
  {
    title: '15. Contact Us',
    body: [
      'For questions or concerns regarding these Terms, contact:',
      'SNN Management Inc',
      'Attn: Legal Department',
      'Salt Lake City, Utah, USA',
      'legal@complin.ai'
    ]
  }
];

const TermsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <header className="mb-10">
          <p className="text-xs uppercase tracking-wider text-gray-500">Terms and Conditions</p>
          <h1 className="text-3xl font-semibold mt-2 text-gray-900">Complin.AI Terms and Conditions</h1>
          <p className="mt-3 text-sm text-gray-600">Last updated: November 6, 2025</p>
          <p className="mt-1 text-sm text-gray-600">Developer: SNN Management Inc, Salt Lake City, Utah, USA</p>
        </header>

        <section className="space-y-8 text-sm leading-6 text-gray-700">
          {sections.map((section) => (
            <article key={section.title}>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{section.title}</h2>
              {section.body.map((paragraph, idx) => (
                <p key={idx} className="mb-2 whitespace-pre-line">
                  {paragraph}
                </p>
              ))}
            </article>
          ))}
        </section>
      </div>
    </div>
  );
};

export default TermsPage;

